import { ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ColumnDefinition, DataRow } from 'src/types/tableDataSet'
import { getCellContent, getExcelColumnLabel } from 'src/utils/tableUtils'

import './SpreadsheetTable.css'

interface SpreadsheetTableProps {
  columnsDef: ColumnDefinition[]
  rows: DataRow[]
  pageSize?: number
  currentPage?: number
  cornerHeader?: ReactNode
  idColumnIndex?: number
}

export default function SpreadsheetTable({
  columnsDef,
  rows,
  pageSize = 50,
  currentPage = 0,
  cornerHeader,
  idColumnIndex = 0
}: SpreadsheetTableProps) {
  const tableStructure = useMemo(() => {
    if (!columnsDef) return []

    return columnsDef.map((col) => ({
      key: col.name,
      def: col
    }))
  }, [columnsDef])

  if (!tableStructure.length || !rows) {
    return (
      <div className="spreadsheet-empty">
        <FormattedMessage id="no-data" />
      </div>
    )
  }

  return (
    <div className="spreadsheet-container">
      <table>
        <thead>
          <tr>
            <th className="sticky-corner">{cornerHeader ?? ''}</th>
            {tableStructure.map((col, index) => (
              <th key={col.key} className="sticky-top">
                {col.def.name || getExcelColumnLabel(index)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            if (!row) return null
            const rowId = row[idColumnIndex] ?? rowIndex
            const visualRowIndex = currentPage * pageSize + rowIndex + 1
            const uniqueKey = `${String(rowId)}-${visualRowIndex}`
            return (
              <tr key={uniqueKey}>
                <th className="sticky-left">{visualRowIndex}</th>
                {tableStructure.map((colStructure, colIndex) => (
                  <td key={`${String(rowId)}-${colStructure.key}`}>
                    {getCellContent(row[colIndex], colStructure.def)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
